import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Empty } from "antd";

import { UTILS } from "utils";
import { CONSTANTS as C } from "config";
import { SettingsAction,AreasAction } from "redux/actions";
import useUserType from "hooks/useUserType";
import useAllStores from "hooks/useCurrentUserStores";

import {
  useGetBrandInfoWithIDMutation,
  useUpdateShippingMethodStatusMutation,
} from "redux/shipping-methods/shipping-service";

import AppContainer from "components/ui/AppContainer";
import AppAlertBox from "components/ui/AppAlertBox";
import AppSelect from "components/ui/AppSelect";
import AppButton from "components/ui/AppButton";
import ShippingMethodsList from "../common/ShippingMethodsList";

import ManageAreas from "../areas/containers/ManageAreas";
import ManageCities from "../cities/containers/ManageCities";
import ManageCountries from "../countries/containers/ManageCountries";
import ManageMappedAreas from "../mapped-areas/ManageMappedAreas";

const ShipmentDelivery = ({ as = "page", onSaveAndContinue }) => {
  const { t } = useTranslation();
  const userType = useUserType();

  const { stores } = useAllStores();

  const [shippingInfo, setShippingInfo] = useState({
    activeMethod: {},
    brandInfo: {},
  });

  const [selectedStore, setSelectedStore] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState(null);

  const [modal, setModal] = useState({
    switchModal: false,
    disableModal: false,
    title: "",
    description: "",
    switch: false,
    data: {},
    successBtnText: "",
    cancelBtnText: "",
  });

  const dispatch = useDispatch();
  const { CatalogueType } = useSelector((state) => state?.SettingsReducer);
  const { isLoading } = useSelector((state) => state.LoaderReducer);
  const [getBrandInfoWithID] = useGetBrandInfoWithIDMutation();
  const [updateShippingMethodStatus] = useUpdateShippingMethodStatusMutation();

  const haspMap = {
    [C.SHIPPING_METHODS.AREA_BASED]: {
      component: ManageAreas,
      action: AreasAction
    },
    [C.SHIPPING_METHODS.CITY_BASED]: {
      component: ManageCities,
      action: AreasAction
    },
    [C.SHIPPING_METHODS.COUNTRY_BASED]: {
      component: ManageCountries,
      action: AreasAction
    },
    [C.SHIPPING_METHODS.MAPPED_AREA_BASED]: {
      component: ManageMappedAreas,
      action: AreasAction
    },
  };

  const defaultDeliveryMethod = [
    { 
      value: C.SHIPPING_METHODS.AREA_BASED,
      label: C.SHIPPING_METHODS.AREA_BASED_DELIVERY_METHOD,
      methodName: C.SHIPPING_METHODS.AREA_BASED,
    },
    {
      value: C.SHIPPING_METHODS.MAPPED_AREA_BASED,
      label: C.SHIPPING_METHODS.MAPPED_AREA_BASED_DELIVERY_METHOD,
      methodName: C.SHIPPING_METHODS.MAPPED_AREA_BASED,
    },
    {
      value: C.SHIPPING_METHODS.CITY_BASED,
      label: C.SHIPPING_METHODS.CITY_BASED_DELIVERY_METHOD,
      methodName: C.SHIPPING_METHODS.CITY_BASED,
    },
    {
      value: C.SHIPPING_METHODS.COUNTRY_BASED,
      label: C.SHIPPING_METHODS.COUNTRY_BASED_DELIVERY_METHOD,
      methodName: C.SHIPPING_METHODS.COUNTRY_BASED,
    }
  ]

  const defaultDeliveryMethodByCatalogue =
    CatalogueType === C.OUTLET_BASED
      ? defaultDeliveryMethod
      : defaultDeliveryMethod.slice(0, 2);

  const deliveryMethodOptions = shippingInfo?.brandInfo?.deliveryMethods?.length
    ? shippingInfo?.brandInfo?.deliveryMethods?.map((item) => ({
        value: item?._id,
        label: item?.methodName?.replaceAll("_", " "),
        ...item,
      }))
    : defaultDeliveryMethodByCatalogue;

  const handleMethodState = (data) => {
    const methods = (data?.brand?.deliveryMethods || []).filter(
      (m) => m?.methodName
    );
    const activeMethod = methods.find((item) => item?.status);

    // Keep `deliveryMethod` in sync with latest server state so UI (switch) updates.
    setDeliveryMethod((prev) => {
      if (prev?._id) {
        return (
          methods.find((m) => m?._id === prev._id) ||
          activeMethod ||
          methods[0] ||
          prev ||
          defaultDeliveryMethodByCatalogue[0] ||
          null
        );
      }
      return (
        activeMethod ||
        methods[0] ||
        prev ||
        defaultDeliveryMethodByCatalogue[0] ||
        null
      );
    });
    setShippingInfo((prevState) => ({
      ...prevState,
      activeMethod: activeMethod ?? {},
    }));
  };

  const returnButtonStatus = (shippingMethodName, toggleSwitchValue) => {
    if (userType === C.USER_TYPE.MANAGER) return true;
    return !!isAllowed(shippingMethodName) && toggleSwitchValue;
  };

  const isAllowed = (method) => {
    if (CatalogueType === C.OUTLET_BASED) {
      if (
        method === C.SHIPPING_METHODS.CITY_BASED ||
        method === C.SHIPPING_METHODS.COUNTRY_BASED
      )
        return true;
      else return false;
    } else return false;
  };

  const getBrandInfoWithId = async () => {
    const userDetail = UTILS.getCurrentUser();
    const { data } = await getBrandInfoWithID({
      brandId: userDetail.brandId,
    });
    if (
      data?.success === 1 &&
      data?.data?.brand &&
      data?.data?.brand?.catalogueType
    ) {
      setShippingInfo((prevState) => ({
        ...prevState,
        brandInfo: data?.data?.brand,
      }));
      dispatch(
        SettingsAction.updateCatalogueType(data.data.brand.catalogueType)
      );
      dispatch(SettingsAction.setBrand(data.data.brand));
      handleMethodState(data.data);
    }
  };

  const closeModal = () => {
    setModal((prevState) => ({
      ...prevState,
      switchModal: false,
      disableModal: false,
    }));
  };

  const disableMethod = async () => {
    await updateShippingMethodStatus(modal.data);
    await getBrandInfoWithId();
    closeModal();
  };

  /* toggling the shipping method. */
  const toggleShippingMethod = UTILS.withTryCatch(async (key, val) => {
    const methodKey =
      key ||
      deliveryMethod?.methodName ||
      deliveryMethod?.value ||
      null;
    if (!methodKey) return;
    if (returnButtonStatus(methodKey, val)) return;
    const userDetail = UTILS.getCurrentUser();
    let obj = {
      brandId: userDetail.brandId,
      deliveryMethod: methodKey,
      deliveryMethodStatus: val,
    };

    setModal((prevState) => ({
      ...prevState,
      data: obj,
    }));

    // disabling active method
    if (shippingInfo?.activeMethod?.methodName && !val) {
      setModal((prevState) => ({
        ...prevState,
        disableModal: true,
        switchModal: false,
        description: t("Do you want to disable the active delivery method"),
        successBtnText: t("yes"),
        cancelBtnText: t("no"),
      }));
    }

    if (val) {
      // switch to other method
      if (shippingInfo?.activeMethod?.methodName) {
        let des = t("It is possible to enable only one delivery method");
        setModal((prevState) => ({
          ...prevState,
          switchModal: true,
          disableModal: false,
          description: des,
          successBtnText: t("okay"),
          cancelBtnText: false,
        }));
      }
      //activating new  method
      else {
        await updateShippingMethodStatus(obj);
      }
    }
    await getBrandInfoWithId();
  });

  const onStoreChange = (value) => {
    const selected = stores?.find((store) => store.value === value) || null;
    dispatch(AreasAction.clearShippingAreaTableData());
    setSelectedStore(selected);
    localStorage.setItem("storeId", value);
    if (selected) {
      dispatch(AreasAction.setStore({ ...selected, _id: selected?.value }));
    }
  };

  useEffect(() => {
    if (!stores?.length) return;
    if (selectedStore?.value) return;

    const saved = localStorage.getItem("storeId");
    const savedOption = saved
      ? stores.find((store) => store.value === saved) || null
      : null;

    const initial = savedOption || stores[0];
    if (initial?.value) {
      onStoreChange(initial.value);
    }
  }, [stores]);

  const Component =
    haspMap[deliveryMethod?.methodName]?.component ??
    (() => <Empty description={t("No data found")} />);

  useEffect(() => {
    getBrandInfoWithId();
  }, []);

  const footer =
    typeof onSaveAndContinue === "function" ? (
      <div className="flex justify-end">
        <AppButton onClick={() => onSaveAndContinue?.()}>
          {t("Save & Continue")}
        </AppButton>
      </div>
    ) : null;

  return (
    <>
      <AppAlertBox
        open={modal.disableModal}
        {...{ setModal, isLoading, closeModal, modal }}
        PressYes={disableMethod}
      />
      <AppAlertBox
        open={modal.switchModal}
        {...{ setModal, isLoading, modal }}
        PressYes={closeModal}
      />
      <AppContainer
        title={t("Shipping & Delivery")}
        subTitle={t("Manage your shipping and delivery methods")}
        loading={isLoading}
        footer={footer}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-4">
          <AppSelect
            label={t("Delivery Method")}
            placeholder={t("Select Delivery Method")}
            options={defaultDeliveryMethodByCatalogue}
            value={deliveryMethod?.methodName}
            onChange={(v) => {
              dispatch(AreasAction.clearShippingAreaTableData());
              const fromBackend = deliveryMethodOptions?.find(
                (item) => item?.methodName === v
              );
              const fromDefaults = defaultDeliveryMethodByCatalogue?.find(
                (item) => item?.methodName === v
              );
              setDeliveryMethod(
                fromBackend
                  ? { ...fromDefaults, ...fromBackend, methodName: fromBackend?.methodName || v }
                  : fromDefaults || null
              );
            }}
          />

          <AppSelect
            label={t("Select Store")}
            placeholder={t("Select Store")}
            options={stores}
            value={selectedStore?.value}
            disabled={!deliveryMethod?._id}
            onChange={(v) => onStoreChange(v)}
          />
          <ShippingMethodsList
            method={deliveryMethod}
            toggleShippingMethod={toggleShippingMethod}
            CatalogueType={CatalogueType}
          />
        </div>
        <Component  key={`${deliveryMethod?.methodName}-${selectedStore?.value}`}/>
      </AppContainer>
    </>
  );
};

export default ShipmentDelivery;
